'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { pick, debounce, isEqual } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import EditorTypes from 'ContentUtils/constants/EditorTypes';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITextInput from 'UIComponents/input/UITextInput';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import UIAlert from 'UIComponents/alert/UIAlert';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import WidgetIcon from 'ContentComponents/cmv2/WidgetIcon';
import { GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import Tool from 'DesignComponents/tool/Tool';
import ToolBreadcrumbs from 'DesignComponents/tool/ToolBreadcrumbs';
import ToolExpandableSection from 'DesignComponents/tool/ToolExpandableSection';
import ToolSection from 'DesignComponents/tool/ToolSection';
import ToolSections from 'DesignComponents/tool/ToolSections';
import { CONTENT_OPTIONS, EDITOR_OPTIONS, DISPLAY_CONDITIONS, REPEATER_OPTIONS } from 'DesignData/constants/AccordionKeys';
import { MIN, CHOICES, DEFAULT, VALIDATION_REGEX, OBJECT_TYPE, SUPPORTED_SOURCE_TYPES, TABLE_NAME_OR_ID } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { getIsOpen } from 'DesignData/helpers/accordionHelpers';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import CommandBar from 'DesignEditorsUI/components/shared/controls/commandBar/CommandBar';
import CommandBarButton from 'DesignEditorsUI/components/shared/controls/commandBar/CommandBarButton';
import FieldLabel from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/FieldLabel';
import EditorOptions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/EditorOptions';
import ContentOptions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/contentOptions/ContentOptions';
import ErrorIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/ErrorIcon';
import WarningIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/WarningIcon';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import CopySnippetButton from 'DesignEditorsUI/components/customWidgetEditor/sidebar/CopySnippetButton';
import DisplayConditions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/DisplayConditions';
import RepeaterOptions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/RepeaterOptions';
import FieldGroupChildrenList from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/FieldGroupChildrenList';
import FieldGrouping from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FieldGrouping';
import GlobalContentAlert from 'DesignEditorsUI/components/shared/globalContent/GlobalContentAlert';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.${key}`;

const errorI18nKey = key => `DesignEditorsUI.customWidgetEditor.errors.field.${key}`;

class FieldEditSidebar extends PureComponent {
  constructor(props) {
    super(props);

    this.onBreadcrumbsLinkClick = fieldId => {
      const {
        customWidgetId,
        setSelectedField
      } = this.props;
      setSelectedField(customWidgetId, {
        id: fieldId
      });
    };

    this.editField = changes => {
      const {
        customWidgetId
      } = this.props;
      const {
        field
      } = this.state;
      const updatedField = field.merge(changes);
      this.setState({
        field: updatedField
      });
      this.debouncedEditField(customWidgetId, updatedField);
    };

    this.debouncedEditField = debounce(function debouncedEditField(customWidgetId, updatedField) {
      this.props.editField(customWidgetId, updatedField);
    }, 1000);

    this.onCloneClick = () => {
      this.props.cloneField(this.props.customWidgetId, this.props.field.id);
    };

    this.onDeleteClick = () => {
      this.props.deleteField(this.props.customWidgetId, this.props.field.id);
    };

    this.onNameChange = e => {
      const name = e.target.value;
      this.editField({
        name
      });
    };

    this.onPreviewClick = () => {
      this.props.setFullScreenEditorOpen(this.props.customWidgetId, !this.props.isFullScreenEditorOpen);
    };

    this.onOpenChange = key => {
      return evt => {
        this.props.saveAccordionState(key, evt.target.value);
      };
    };

    this.state = {
      field: props.field
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.field, this.props.field)) {
      this.setState({
        field: this.props.field
      });
    }
  }

  get contentOptionsErrors() {
    const {
      fieldErrors
    } = this.props;
    return pick(fieldErrors, [MIN, CHOICES, DEFAULT, VALIDATION_REGEX, OBJECT_TYPE, SUPPORTED_SOURCE_TYPES, TABLE_NAME_OR_ID]);
  }

  get contentOptionsWarnings() {
    const {
      fieldWarnings
    } = this.props;
    return pick(fieldWarnings, [MIN, CHOICES, DEFAULT, VALIDATION_REGEX, OBJECT_TYPE, SUPPORTED_SOURCE_TYPES, TABLE_NAME_OR_ID]);
  }

  get fieldName() {
    const breadcrumbsPath = this.props.fieldBreadcrumbs.map(_field => _field.name).join('.');
    return breadcrumbsPath || this.state.field.name;
  }

  getIsOpen(key) {
    return getIsOpen(this.props.accordionStates, [this.state.field.id, key]);
  }

  renderFieldGroupErrors() {
    if (this.props.isFieldGroupingActive) {
      return null;
    }

    const {
      children = []
    } = this.props.fieldErrors;
    const errors = children.map(({
      type,
      data
    }) => {
      return /*#__PURE__*/_jsx(Small, {
        use: "error",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: errorI18nKey(type),
          options: data
        })
      }, type);
    });
    return /*#__PURE__*/_jsx("div", {
      className: "p-x-6 p-y-3",
      children: errors
    });
  }

  renderFieldGroupWarnings() {
    if (this.props.isFieldGroupingActive) {
      return null;
    }

    const {
      children = []
    } = this.props.fieldWarnings;
    const warnings = children.map(({
      type,
      data
    }) => {
      return /*#__PURE__*/_jsx(Small, {
        use: "error",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: errorI18nKey(type),
          options: data
        })
      }, type);
    });
    return /*#__PURE__*/_jsx("div", {
      className: "p-x-6 p-y-3",
      children: warnings
    });
  }

  renderCopyDropdown() {
    const {
      field
    } = this.state;

    const buttonText = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('copyDropdown')
      })
    });

    return /*#__PURE__*/_jsx(UIDropdown, {
      buttonUse: "transparent",
      buttonClassName: "p-all-0",
      buttonText: buttonText,
      menuWidth: 150,
      placement: "bottom left",
      popoverProps: {
        className: 'copy-dropdown'
      },
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [/*#__PURE__*/_jsx(CopySnippetButton, {
          customWidgetId: this.props.customWidgetId,
          field: field,
          fieldName: this.fieldName,
          hostTemplateTypes: this.props.customWidgetHostTemplateTypes,
          contentTypes: this.props.customWidgetContentTypes
        }), /*#__PURE__*/_jsx(CopySnippetButton, {
          customWidgetId: this.props.customWidgetId,
          field: field,
          fieldName: this.fieldName,
          hostTemplateTypes: this.props.customWidgetHostTemplateTypes,
          contentTypes: this.props.customWidgetContentTypes,
          useFullSnippet: false
        })]
      })
    });
  }

  renderNameInput() {
    const {
      fieldErrors,
      isFieldGroupingActive,
      readOnly
    } = this.props;
    const {
      field
    } = this.state;

    if (isFieldGroupingActive) {
      return null;
    }

    return /*#__PURE__*/_jsx(ToolFieldset, {
      children: /*#__PURE__*/_jsx(FormControlWithErrors, {
        className: "field-name-input",
        errors: fieldErrors.name,
        help: this.renderCopyDropdown(),
        label: text(i18nKey('nameLabel')),
        children: /*#__PURE__*/_jsx(SidebarInput, {
          className: "is--code",
          inputComponent: UITextInput,
          onChange: this.onNameChange,
          value: field.name,
          disabled: readOnly
        })
      })
    });
  }

  renderContentOptions() {
    const {
      isEmailOrAnyType,
      customWidgetId,
      customWidgetPath,
      customWidgetContentTypes,
      customWidgetHostTemplateTypes,
      isFieldGroupingActive,
      readOnly,
      isV1,
      isGlobal,
      hasGlobalContentEditorAccess
    } = this.props;
    const {
      field
    } = this.state;

    if (field.type === GROUP) {
      return /*#__PURE__*/_jsx(ToolSection, {
        children: /*#__PURE__*/_jsx(FieldGroupChildrenList, {
          customWidgetId: customWidgetId,
          fieldName: this.fieldName,
          field: field,
          isFieldGroupingActive: isFieldGroupingActive,
          readOnly: readOnly
        })
      });
    }

    const accordionTitle = /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: this.contentOptionsErrors,
        i18nKey: i18nKey('contentOptions.hasFieldError')
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: "m-right-2",
        fieldWarnings: this.contentOptionsWarnings,
        i18nKey: i18nKey('contentOptions.hasFieldWarning')
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('contentOptions.accordionLabel')
      })]
    });

    return /*#__PURE__*/_jsxs(ToolExpandableSection, {
      defaultOpen: true,
      titleText: accordionTitle,
      onOpenChange: this.onOpenChange(CONTENT_OPTIONS),
      open: this.getIsOpen(CONTENT_OPTIONS),
      children: [isGlobal && hasGlobalContentEditorAccess && /*#__PURE__*/_jsx(GlobalContentAlert, {
        className: "m-bottom-8 p-top-5",
        contentPath: customWidgetPath
      }), /*#__PURE__*/_jsx(ContentOptions, {
        customWidgetContentTypes: customWidgetContentTypes,
        customWidgetHostTemplateTypes: customWidgetHostTemplateTypes,
        editor: EditorTypes.DESIGN_MANAGER,
        isEmailOrAnyType: isEmailOrAnyType,
        customWidgetId: customWidgetId,
        isV1: isV1,
        editField: this.editField,
        field: field,
        fieldErrors: this.contentOptionsErrors,
        fieldWarnings: this.contentOptionsWarnings,
        compact: true,
        readOnly: readOnly,
        onPreviewClick: this.onPreviewClick
      })]
    });
  }

  renderAccordions() {
    const {
      customWidgetId,
      fieldErrors,
      fieldWarnings,
      isFieldGroupingActive,
      isGlobal,
      readOnly,
      isV1,
      isStyleTab
    } = this.props;
    const {
      field
    } = this.state;
    const contentOptions = this.renderContentOptions();

    if (isFieldGroupingActive) {
      return contentOptions;
    }

    const contentOptionsWithKey = /*#__PURE__*/cloneElement(contentOptions, {
      key: `${field.id}-content-options`
    });

    const editorOptions = /*#__PURE__*/_jsx(ToolExpandableSection, {
      titleText: text(i18nKey('editorOptions.accordionLabel')),
      onOpenChange: this.onOpenChange(EDITOR_OPTIONS),
      open: this.getIsOpen(EDITOR_OPTIONS),
      children: /*#__PURE__*/_jsx(EditorOptions, {
        editField: this.editField,
        field: field,
        fieldErrors: fieldErrors,
        isGlobal: isGlobal,
        readOnly: readOnly,
        isV1: isV1
      })
    }, `${field.id}-editor-options`);

    const repeaterOptions = isStyleTab ? null : /*#__PURE__*/_jsx(RepeaterOptions, {
      editField: this.editField,
      field: field,
      fieldErrors: fieldErrors,
      fieldWarnings: fieldWarnings,
      onOpenChange: this.onOpenChange(REPEATER_OPTIONS),
      open: this.getIsOpen(REPEATER_OPTIONS),
      readOnly: readOnly
    }, `${field.id}-repeater-options`);

    if (isV1) {
      return [contentOptionsWithKey, editorOptions];
    }

    return [contentOptionsWithKey, editorOptions, /*#__PURE__*/_jsx(DisplayConditions, {
      customWidgetId: customWidgetId,
      editField: this.editField,
      field: field,
      fieldErrors: fieldErrors,
      fieldWarnings: fieldWarnings,
      onOpenChange: this.onOpenChange(DISPLAY_CONDITIONS),
      open: this.getIsOpen(DISPLAY_CONDITIONS),
      readOnly: readOnly
    }, `${field.id}-display-conditions`), repeaterOptions];
  }

  renderGroupControls() {
    const {
      customWidgetId,
      isFieldGroupingActive
    } = this.props;

    if (!isFieldGroupingActive) {
      return null;
    }

    return /*#__PURE__*/_jsx(ToolSection, {
      flush: true,
      children: /*#__PURE__*/_jsx(FieldGrouping, {
        customWidgetId: customWidgetId
      })
    });
  }

  renderBreadcrumbs() {
    const {
      fieldBreadcrumbs,
      customWidgetIcon,
      isFieldGroupingActive,
      isFullScreenEditorOpen
    } = this.props;

    const breadcrumbsIcon = /*#__PURE__*/_jsx(WidgetIcon, {
      alignment: "text-bottom",
      size: 14,
      icon: customWidgetIcon
    });

    return /*#__PURE__*/_jsx(ToolBreadcrumbs, {
      className: "field-edit-breadcrumbs",
      isReadOnly: isFieldGroupingActive || isFullScreenEditorOpen,
      items: fieldBreadcrumbs,
      onLinkClick: this.onBreadcrumbsLinkClick,
      rootIcon: breadcrumbsIcon
    });
  }

  renderCannotEditFieldTypeAlert() {
    if (this.props.canEditFieldType) return null;
    const messageI18nKey = this.props.canCreateFieldType ? 'missingEditPermission' : 'missingEditAndCreatePermission';
    return /*#__PURE__*/_jsx(UIAlert, {
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(messageI18nKey)
      })
    });
  }

  renderFieldEditControls() {
    const {
      customWidgetId,
      isFieldGroupingActive,
      readOnly,
      canCreateFieldType
    } = this.props;
    const {
      field
    } = this.state;
    let cloneI18nKey = field.type === GROUP ? 'cloneGroup' : 'cloneField';

    if (!readOnly && !canCreateFieldType) {
      cloneI18nKey = 'missingCreatePermission';
    }

    const deleteI18nKey = field.type === GROUP ? 'deleteGroup' : 'deleteField';
    const fieldEditReadOnly = isFieldGroupingActive || readOnly;
    return /*#__PURE__*/_jsxs(ToolSections, {
      className: "field-edit-controls",
      children: [this.renderCannotEditFieldTypeAlert(), /*#__PURE__*/_jsx(FieldLabel, {
        customWidgetId: customWidgetId,
        editField: this.editField,
        field: field,
        isReadOnly: fieldEditReadOnly
      }), /*#__PURE__*/_jsxs(CommandBar, {
        children: [/*#__PURE__*/_jsx(CommandBarButton, {
          title: text(i18nKey(cloneI18nKey)),
          iconName: "duplicate",
          onClick: this.onCloneClick,
          isReadOnly: fieldEditReadOnly || !canCreateFieldType
        }), /*#__PURE__*/_jsx(CommandBarButton, {
          title: text(i18nKey(deleteI18nKey)),
          iconName: "delete",
          onClick: this.onDeleteClick,
          isReadOnly: fieldEditReadOnly
        })]
      }), this.renderGroupControls(), this.renderNameInput(), /*#__PURE__*/_jsx(ToolSection, {
        flush: true,
        children: this.renderAccordions()
      }), this.renderFieldGroupErrors(), this.renderFieldGroupWarnings()]
    });
  }

  render() {
    const {
      customWidgetId,
      isFieldGroupingActive
    } = this.props;
    const {
      field
    } = this.state;

    if (!field) {
      return null;
    }

    const inputsClassName = 'field-edit-inputs' + (isFieldGroupingActive ? " is--field-grouping-active" : "");
    return /*#__PURE__*/_jsx(Tool, {
      breadcrumbsContent: this.renderBreadcrumbs(),
      className: "field-edit-sidebar-container",
      contentClassName: inputsClassName,
      children: this.renderFieldEditControls()
    }, `${customWidgetId}-${field.id}`);
  }

}

FieldEditSidebar.propTypes = {
  canEditFieldType: PropTypes.bool.isRequired,
  canCreateFieldType: PropTypes.bool.isRequired,
  customWidgetContentTypes: PropTypes.array,
  customWidgetHostTemplateTypes: PropTypes.array,
  customWidgetIcon: PropTypes.string,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  customWidgetPath: PropTypes.string.isRequired,
  cloneField: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField),
  fieldBreadcrumbs: PropTypes.array,
  fieldErrors: PropTypes.object,
  fieldWarnings: PropTypes.object,
  isEmailOrAnyType: PropTypes.bool.isRequired,
  isFieldGroupingActive: PropTypes.bool.isRequired,
  isFullScreenEditorOpen: PropTypes.bool.isRequired,
  isGlobal: PropTypes.bool,
  isStyleTab: PropTypes.bool,
  setSelectedField: PropTypes.func.isRequired,
  saveAccordionState: PropTypes.func.isRequired,
  accordionStates: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  isV1: PropTypes.bool.isRequired,
  setFullScreenEditorOpen: PropTypes.func.isRequired,
  hasGlobalContentEditorAccess: PropTypes.bool.isRequired
};
FieldEditSidebar.defaultProps = {
  customWidgetContentTypes: null,
  customWidgetHostTemplateTypes: null,
  customWidgetIcon: null,
  field: null,
  fieldErrors: {},
  fieldWarnings: {},
  fieldBreadcrumbs: []
};
export default FieldEditSidebar;